import { MobileDriver } from '../driver';
import { CanvasOpIOS, FlagsIOS } from '@fullstory/mobile-flatbuffers';
import { CanvasRendererFactory } from '../canvasrender';
import { Canvas, CanvasIOS, View, Rect, Point, Frame } from '../view';
import { IOSCanvasRendererFactory } from './ioscanvasrender';
import { PageTypeEnum } from '@fullstory/client-api';
import { DataUnpack } from '../dataunpack';
import { logOnce } from '../log_once';

// overwrite a canvas op's text with the view text if it looks like
// it's drawing an icon from an icon font
//  - op is drawing only a replacement character (we didn't get the codepoint)
//  - the view text is a single unicode private use area character
function fixUpIconFont(op: Canvas<CanvasOpIOS>, viewText: string) {
  if (op.text !== '\ufffd') return;
  let textLooksLikeIcon = false;
  const viewCodepoint = viewText.codePointAt(0);
  if (0xe000 <= viewCodepoint && viewCodepoint < 0xf900) {
    // this is the main private use area, icon fonts we've seen so far use this
    textLooksLikeIcon = viewText.length === 1;
  } else if (viewCodepoint > 0xf0000) {
    // unicode also reserves two high planes for private use
    logOnce(`not showing possible icon font character (codepoint U+${viewCodepoint.toString(16)})`);
  }
  if (textLooksLikeIcon) {
    op.text = viewText;
  }
}

export class IOSDriver extends MobileDriver<CanvasOpIOS> {
  private _factory: CanvasRendererFactory<CanvasOpIOS>;

  constructor() {
    super();
    this._factory = new IOSCanvasRendererFactory();
  }

  type = PageTypeEnum.PageTypeiOS;
  // prettier-ignore
  defaultCanvasDefintion = Int8Array.from([20, 18, 8, 2, 21, 6, 11, 12, 10, 11, 14, 9, 15, 10, 6, 8, 7, 9, 4, 8, 5, 9, 23, 13,
    3, 4, 54, 2, 50, 9, 51, 7, 16, 10, 17, 10, 2, 34, 12, 8, 19, 66, 9, 6, 55, 7, 52, 18, 53, 2, 18, 6]);
  isBlockedFlag = FlagsIOS.BLOCKED;
  isWebViewFlag = FlagsIOS.WEBVIEW;

  rendererFactory(): CanvasRendererFactory<CanvasOpIOS> {
    return this._factory;
  }

  reprocessCanvas(view: View) {
    // TODO(mmastrac): clipping, text, etc

    if (view.canvas.length >= 2) {
      // we write these clipping commands ourselves if masksToBounds is true
      view.clipsChildrenToBounds =
        view.canvas[0].op === CanvasOpIOS.PATH_RECT &&
        view.canvas[1].op === CanvasOpIOS.PATH_CLIP_NONZERO;
    } else {
      view.clipsChildrenToBounds = false;
    }

    // some versions of Lottie, which seems to be tied to this particular root view
    // (vs "Lottie.AnimationView" or others) confuse our capture by using (CA)Animations
    // that never end. This means CATransactions never commit e.g. position values
    // for us to see, so our playback draws shapes with unintended coordinates,
    // sometimes covering large portions of the screen
    if (view.getSelectorRule()?.tag === 'Lottie.LottieAnimationView') {
      // return without adding DRAW_CHILD ops, making this view and its descendants invisible
      return;
    }

    let hasDrawChild = false;
    let multipleTextOps = false;
    let lastTextOp: Canvas<number> | null = null;
    let unmatchedSaveDepth = 0;

    for (let i = 0; i < view.canvas.length; i++) {
      const entry = view.canvas[i];
      if (entry.op == CanvasOpIOS.DRAW_CHILD) {
        hasDrawChild = true;
      } else if (entry.op === CanvasOpIOS.TEXT_SHOW) {
        if (lastTextOp !== null) multipleTextOps = true;
        lastTextOp = entry;
      } else if (entry.op === CanvasOpIOS.GS_SAVE) {
        unmatchedSaveDepth++;
      } else if (entry.op === CanvasOpIOS.GS_RESTORE) {
        if (unmatchedSaveDepth > 0) unmatchedSaveDepth--;
      }
    }
    if (!hasDrawChild && view.children.length) {
      // we may have accidentally truncated restores which occurred
      // after the last drawing operation. These can't be "real",
      // as we draw iOS views individually during capture
      for (let i = 0; i < unmatchedSaveDepth; i++) {
        const canvas = new CanvasIOS();
        canvas.op = CanvasOpIOS.GS_RESTORE;
        view.canvas.push(canvas);
      }

      for (let i = 0; i < view.children.length; i++) {
        const canvas = new CanvasIOS();
        canvas.op = CanvasOpIOS.DRAW_CHILD;
        canvas.viewId = view.children[i].uniqueId;
        view.canvas.push(canvas);
      }
    }

    // we have high confidence in the text we get directly from the view
    view.textContent = view.viewStateText;

    // Sometimes we don't get codepoint information during capture, leading
    // to drawing replacement characters. Our iOS view text is much more
    // reliable, but matching it up with canvas ops is nontrivial
    // For now, just try to fix up icon fonts, expecting only one text drawing op
    if (lastTextOp && !multipleTextOps && view.textContent && lastTextOp.text) {
      fixUpIconFont(lastTextOp, view.textContent);
    }
  }

  unpackCanvasItem(frame: Frame, canvasJson: any): CanvasIOS {
    const canvas = new CanvasIOS();
    canvas.op = canvasJson.op;
    canvas.text = canvasJson.text || null;
    canvas.bitmap = canvasJson.bitmap || null;
    canvas.ninePatch = DataUnpack.unpackRectArray(canvasJson.ninepatch);
    canvas.shader = canvasJson.shader || null;

    if (canvasJson.srczero)
      canvas.src = new Rect(0, 0, canvasJson.srczero[0], canvasJson.srczero[1]);
    else if (canvasJson.src) canvas.src = DataUnpack.unpackRectArray(canvasJson.src);
    else canvas.src = Rect.ZERO();

    if (canvasJson.destzero)
      canvas.dest = new Rect(0, 0, canvasJson.destzero[0], canvasJson.destzero[1]);
    else if (canvasJson.dest) canvas.dest = DataUnpack.unpackRectArray(canvasJson.dest);
    else canvas.dest = Rect.ZERO();

    canvas.matrix = DataUnpack.unpackIOSMatrix6Array(canvasJson.matrix6);

    // note: points at 0,0 are omitted from the flatbuffer and must be recreated here
    // TODO(nathan): consider omitting this from ops that don't need a point
    if (canvasJson.point) canvas.point = DataUnpack.unpackPointArray(canvasJson.point);
    else if (canvasJson.pointf) canvas.point = DataUnpack.unpackPointArray(canvasJson.pointf);
    else canvas.point = new Point(0, 0);

    if (canvasJson.pointf2) canvas.point2 = DataUnpack.unpackPointArray(canvasJson.pointf2);
    else if (
      canvas.op === CanvasOpIOS.PATH_CURVE_TO ||
      canvas.op === CanvasOpIOS.PATH_CURVE_TO23 ||
      canvas.op === CanvasOpIOS.PATH_CURVE_TO13
    )
      // only these ops use a second point
      canvas.point2 = new Point(0, 0);

    if (canvasJson.pointf3) canvas.point3 = DataUnpack.unpackPointArray(canvasJson.pointf3);
    else if (canvas.op === CanvasOpIOS.PATH_CURVE_TO)
      // only this op uses a third point
      canvas.point3 = new Point(0, 0);

    canvas.value = canvasJson.value || 0;
    canvas.flag1 = canvasJson.flag1 || 0;
    canvas.lineWidth = canvasJson.linewidth;

    canvas.textSize = canvasJson.textsize;
    canvas.textFont = canvasJson.textfont || null;
    canvas.textCharSpacing = canvasJson.textcharspacing || 0;
    canvas.textRender = canvasJson.textrendermode || 0;
    if (canvasJson.textbounds) {
      canvas.textBounds = DataUnpack.unpackPointArray(canvasJson.textbounds);
      if (frame._fudgeIOSTextBounds) {
        // we used to reduce the text width by a fixed fudge factor during playback,
        // but now that we track more layout info this is no longer necessary
        // this flag is set based on the frame buffer protocol version in dataunpack.ts
        canvas.textBounds.x *= 0.975;
      }
    }

    canvas.scaleOverride = canvasJson.scaleoverride;
    canvas.textPrivate = canvasJson.painttextprivate === null;

    canvas.color = DataUnpack.unpackCssColor(canvasJson.color);

    return canvas;
  }
}
